type SiteMetadata = {
  site: {
    name: string;
    domain: string;
    url: string;
  };
  seo: {
    title: Record<string, string>;
    description: Record<string, string>;
    keywords: Record<string, string[]>;
    openGraph: {
      type: 'website' | 'profile' | 'article';
      siteName: Record<string, string>;
    };
    twitter: {
      card: 'summary' | 'summary_large_image' | 'app' | 'player';
      site: string;
      creator: string;
    };
    robots: {
      index: boolean;
      follow: boolean;
      crawlDelay?: number;
    };
    languages: {
      supported: string[];
      default: string;
    };
  };
  social: {
    linkedin: string;
    github: string;
    twitter: string;
    blog: string;
  };
  author: {
    name: string;
    jobTitle: Record<string, string>;
    description: Record<string, string>;
    location: Record<string, string>;
    alumniOf: Array<{
      name: Record<string, string>;
      type: string;
    }>;
    knowsAbout: Record<string, string[]>;
    knowsLanguage: Record<string, string[]>;
  };
  images: {
    social: {
      url: string;
      width: number;
      height: number;
      alt: Record<string, string>;
    };
    profile: string;
    favicon: {
      svg: string;
      png: string;
      apple: string;
    };
  };
};

const metadata: SiteMetadata = {
  site: {
    name: "Manoel Lemos",
    domain: "manoellemos.com",
    url: "https://manoellemos.com"
  },
  seo: {
    title: {
      en: "Manoel Lemos - Venture Capitalist & Technology Entrepreneur",
      pt: "Manoel Lemos - Venture Capitalist & Empreendedor de Tecnologia"
    },
    description: {
      en: "Brazilian VC and computer engineer connecting Silicon Valley and Brazil, supporting founders and companies adopting AI and emerging technologies.",
      pt: "VC brasileiro e engenheiro de computação conectando o Vale do Silício ao Brasil, apoiando fundadores e empresas que adotam IA e tecnologias emergentes."
    },
    keywords: {
      en: [
        "Venture Capital",
        "Technology Entrepreneur", 
        "Angel Investor",
        "Silicon Valley",
        "Brazil Tech Ecosystem",
        "Artificial Intelligence",
        "Startup Advisor",
        "Emerging Technologies",
        "Computer Engineer",
        "@mlemos",
        "Araguari"
      ],
      pt: [
        "Venture Capital",
        "Empreendedor de Tecnologia",
        "Investidor Anjo",
        "Vale do Silício",
        "Ecossistema de Tecnologia do Brasil",
        "Inteligência Artificial",
        "Mentor de Startups",
        "Tecnologias Emergentes",
        "Engenheiro da Computação",
        "@mlemos",
        "Araguari"
      ]
    },
    openGraph: {
      type: "profile",
      siteName: {
        en: "Manoel Lemos",
        pt: "Manoel Lemos"
      }
    },
    twitter: {
      card: "summary_large_image",
      site: "@mlemos",
      creator: "@mlemos"
    },
    robots: {
      index: true,
      follow: true,
      crawlDelay: 1
    },
    languages: {
      supported: ["en", "pt"],
      default: "en"
    }
  },
  social: {
    linkedin: "https://www.linkedin.com/in/mlemos",
    github: "https://github.com/mlemos",
    twitter: "https://x.com/mlemos",
    blog: "https://blog.manoellemos.com"
  },
  author: {
    name: "Manoel Lemos",
    jobTitle: {
      en: "Venture Capitalist & Technology Entrepreneur",
      pt: "Venture Capitalist & Empreendedor de Tecnologia"
    },
    description: {
      en: "Brazilian computer engineer and venture capitalist connecting Silicon Valley innovation with Brazil's entrepreneurial ecosystem. Supporting founders and companies adopting artificial intelligence and emerging technologies.",
      pt: "Engenheiro da computação e venture capitalist brasileiro conectando a inovação do Vale do Silício com o ecossistema empreendedor do Brasil. Apoiando fundadores e empresas que adotam inteligência artificial e tecnologias emergentes."
    },
    location: {
      en: "Silicon Valley, CA, USA & São Paulo, SP, Brazil",
      pt: "Vale do Silício, CA, EUA e São Paulo, SP, Brasil"
    },
    alumniOf: [
      {
        name: {
          en: "UNICAMP - State University of Campinas",
          pt: "UNICAMP - Universidade Estadual de Campinas"
        },
        type: "CollegeOrUniversity"
      },
      {
        name: {
          en: "Stanford Graduate School of Business",
          pt: "Stanford Graduate School of Business"
        },
        type: "CollegeOrUniversity"
      },
      {
        name: {
          en: "MIT Sloan School of Management",
          pt: "MIT Sloan School of Management"
        },
        type: "CollegeOrUniversity"
      }
    ],
    knowsAbout: {
      en: [
        "Startups",
        "Venture Capital",
        "Entrepreneurship",
        "Science & Technology", 
        "Artificial Intelligence",
        "Emerging Technologies",
        "Brazil Tech Ecosystem",
        "Silicon Valley"
      ],
      pt: [
        "Startups",
        "Venture Capital",
        "Empreendedorismo",
        "Ciência e Tecnologia",
        "Inteligência Artificial",
        "Tecnologias Emergentes",
        "Ecossistema de Tecnologia do Brasil",
        "Vale do Silício"
      ]
    },
    knowsLanguage: {
      en: ["English", "Portuguese"],
      pt: ["Inglês", "Português"]
    }
  },
  images: {
    social: {
      url: "/social-image.png",
      width: 1200,
      height: 630,
      alt: {
        en: "Manoel Lemos - Venture Capitalist & Technology Entrepreneur",
        pt: "Manoel Lemos - Venture Capitalist & Empreendedor de Tecnologia"
      }
    },
    profile: "/images/header-photo.png",
    favicon: {
      svg: "/favicon.svg",
      png: "/favicon-96x96.png", 
      apple: "/apple-touch-icon.png"
    }
  }
};

export default metadata;

// Utility functions for international SEO
export function getCanonicalUrl(locale: string, path: string = ""): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${metadata.site.url}/${locale}${cleanPath}`;
}

export function getHreflangLinks(path: string = ""): Array<{hreflang: string, href: string}> {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  
  return [
    ...metadata.seo.languages.supported.map(lang => ({
      hreflang: lang,
      href: `${metadata.site.url}/${lang}${cleanPath}`
    })),
    {
      hreflang: "x-default", 
      href: `${metadata.site.url}/${metadata.seo.languages.default}${cleanPath}`
    }
  ];
}

export function getOpenGraphLocale(locale: string): string {
  const localeMap: Record<string, string> = {
    'en': 'en_US',
    'pt': 'pt_BR'
  };
  return localeMap[locale] || 'en_US';
}

// Get base metadata for a specific locale
export function getBaseMetadataForLocale(locale: string) {
  return {
    title: metadata.seo.title[locale] || metadata.seo.title.en,
    description: metadata.seo.description[locale] || metadata.seo.description.en,
    keywords: metadata.seo.keywords[locale] || metadata.seo.keywords.en,
    robots: {
      index: metadata.seo.robots.index,
      follow: metadata.seo.robots.follow,
    },
    icons: {
      icon: [
        { url: metadata.images.favicon.svg, type: "image/svg+xml" },
        { url: metadata.images.favicon.png, sizes: "96x96", type: "image/png" },
        { url: "/favicon.ico", sizes: "any", type: "image/x-icon" },
      ],
      apple: [
        { url: metadata.images.favicon.apple, sizes: "180x180", type: "image/png" },
      ],
      other: [
        {
          rel: "apple-touch-icon",
          url: metadata.images.favicon.apple,
        },
        {
          rel: "manifest",
          url: "/site.webmanifest",
        }
      ],
    },
    appleWebApp: {
      capable: true,
      title: metadata.site.name,
      statusBarStyle: "default" as const,
    },
    applicationName: metadata.site.name,
    openGraph: {
      title: metadata.seo.title[locale] || metadata.seo.title.en,
      description: metadata.seo.description[locale] || metadata.seo.description.en,
      type: metadata.seo.openGraph.type,
      siteName: metadata.seo.openGraph.siteName[locale] || metadata.seo.openGraph.siteName.en,
      images: { 
        width: metadata.images.social.width, 
        height: metadata.images.social.height, 
        url: metadata.images.social.url,
        alt: metadata.images.social.alt[locale] || metadata.images.social.alt.en
      }
    },
    twitter: {
      title: metadata.seo.title[locale] || metadata.seo.title.en,
      description: metadata.seo.description[locale] || metadata.seo.description.en,
      card: metadata.seo.twitter.card,
      site: metadata.seo.twitter.site,
      creator: metadata.seo.twitter.creator,
      images: { 
        url: metadata.images.social.url,
        alt: metadata.images.social.alt[locale] || metadata.images.social.alt.en
      }
    }
  };
}